import { Box, Button, TextField, styled } from '@mui/material';
import ReplayIcon from '@mui/icons-material/Replay';
import React, { FC, createRef, useEffect, useRef, useState } from 'react';
import { Controller, SubmitHandler, useForm } from 'react-hook-form';
import { Texts } from '@/shared/types';
import { VERIFY_EMAIL_ROUTE } from './route.definitions';
import { useHttpMutation, useHttpQuery } from '@/api';
import { useAlert } from '@/shared/components';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { inviteDestination } from '@/screens/unauthenticated/components/auth-links';
import { API_URLS } from '../consts';
import { useSelector } from 'react-redux';
import { AuthFrame } from '@/screens/unauthenticated/components/main-frame';
import { BhabiStorage } from '@/shared/lib';
import { getUser } from '@/redux/reducers';
import { HOME_ROUTE } from '../home';
import { Store, dispatch } from '@/redux';

export const CodeInput = styled(TextField)(() => ({
  flex: '1 1 0',
  minWidth: 0,
  maxWidth: 56,
  '& .MuiInputBase-root': {
    minHeight: 60,
  },
  '& .MuiInputBase-input': {
    textAlign: 'center',
    fontFamily: 'var(--bh-display)',
    fontSize: 26,
    fontWeight: 800,
    padding: '10px 0',
  },
}));

export const VerifyEmailPageWrapper = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: theme.spacing(4),
  height: '100vh',
}));

export declare type VerificationCode = {
  a: string;
  b: string;
  c: string;
  d: string;
  e: string;
};

export declare type KeysOfVerificationCode = 'a' | 'b' | 'c' | 'd' | 'e';

const defaultValues: VerificationCode = {
  a: '',
  b: '',
  c: '',
  d: '',
  e: '',
};

const keysArray = Object.keys(defaultValues) as KeysOfVerificationCode[];

export interface VerifyEmailPageProps {
  texts?: Texts<'title' | 'submit' | 'resendEmail'>;
}

const useVerifyEmail = () =>
  useHttpMutation<{ message: string }, { code: number }>(
    API_URLS.VERIFY_EMAIL,
    'POST'
  );

export const VerifyEmailPage: FC<VerifyEmailPageProps> = ({
  texts = {
    title: VERIFY_EMAIL_ROUTE.TITLE,
    submit: 'Verify Email',
    resendEmail: 'Haven’t received it? Check your spam folder, or',
    resendButton: 'resend email',
  },
}) => {
  const {
    handleSubmit,
    formState: { errors },
    getValues,
    control,
  } = useForm<VerificationCode>({
    mode: 'onChange',
    defaultValues,
  });

  const { mutate: verifyEmail, isLoading: isVerifying } = useVerifyEmail();
  const email = useSelector((state: Store) => state.userReducer.email);
  const hasErrors = Object.keys(errors).length > 0;
  const { addAlert } = useAlert();

  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const destination =
    inviteDestination(searchParams.get('next')) || HOME_ROUTE.ABSOLUTE_PATH;

  const inputRefs = useRef(keysArray.map(() => createRef<HTMLInputElement>()));

  useEffect(() => {
    inputRefs.current[0].current?.focus();
  }, [inputRefs]);

  const onSubmit: SubmitHandler<VerificationCode> = data => {
    const c = Object.values(data).join('');
    const code = Number(c);

    verifyEmail(
      { code },
      {
        onError: () => {
          addAlert({
            message: 'Error verifying email',
            type: 'error',
          });
        },
        onSuccess: () => {
          addAlert({
            message: 'Email has been verified',
            type: 'success',
          });

          BhabiStorage.getItem(token => {
            if (!token) {
              return;
            }

            dispatch(getUser(token));

            // navigate to the invite that brought them here, else the app
            nav(destination, { replace: true });
          });
        },
      }
    );
  };

  return (
    <AuthFrame
      eyebrow="One last step"
      title={texts.title}
      documentTitle="Verify email"
      subtitle={
        email
          ? `We sent a 5-digit code to ${email}.`
          : 'We sent a 5-digit code to your email.'
      }
    >
      <form
        className="bh-auth__form"
        onSubmit={handleSubmit(onSubmit)}
        noValidate
      >
        <fieldset style={{ border: 0, margin: 0, padding: 0, minWidth: 0 }}>
          <legend className="bh-label" style={{ marginBottom: 10 }}>
            Verification code
          </legend>
          <Box
            display="flex"
            justifyContent="center"
            alignItems="center"
            gap={0.75}
          >
            {keysArray.map((formKey, i) => (
              <Controller
                key={[formKey, i].join('-')}
                control={control}
                name={formKey}
                rules={{
                  required: true,
                  validate: value => value.length === 1,
                }}
                render={({ field: { onChange, ...field } }) => (
                  <CodeInput
                    {...field}
                    inputRef={inputRefs.current[i]}
                    inputProps={{
                      'aria-label': `Digit ${i + 1} of ${keysArray.length}`,
                      inputMode: 'numeric',
                      autoComplete: i === 0 ? 'one-time-code' : 'off',
                      maxLength: 1,
                    }}
                    onKeyDown={e => {
                      if (
                        e.key === 'Backspace' &&
                        !getValues(formKey) &&
                        i > 0
                      ) {
                        inputRefs.current[i - 1].current?.focus();
                      }
                    }}
                    onChange={e => {
                      if (
                        getValues(formKey).length === 1 &&
                        e.target.value.length > 1
                      ) {
                        return;
                      }

                      if (e.target.value && i < inputRefs.current.length - 1) {
                        inputRefs.current[i + 1].current?.focus();
                      }

                      onChange(e);
                    }}
                    error={!!errors[formKey]}
                  />
                )}
              />
            ))}
          </Box>
          {hasErrors && (
            <p
              className="bh-error"
              role="alert"
              style={{ margin: '10px 0 0', textAlign: 'center' }}
            >
              Enter all 5 digits of the code.
            </p>
          )}
        </fieldset>
        <Button
          type="submit"
          variant="contained"
          size="large"
          fullWidth
          disabled={isVerifying}
        >
          {isVerifying ? 'Checking…' : texts.submit}
        </Button>
      </form>
      <div
        className="bh-small bh-muted"
        style={{
          marginTop: 16,
          display: 'grid',
          justifyItems: 'center',
          gap: 2,
          textAlign: 'center',
        }}
      >
        <span>{texts.resendEmail}</span>
        <ResendEmail />
      </div>
    </AuthFrame>
  );
};

export interface ResendEmailProps {
  texts?: Texts<'resendButton'>;
}
export const ResendEmail: FC<ResendEmailProps> = ({
  texts = {
    resendButton: 'Resend Email',
  },
}) => {
  const [enabled, setFetch] = useState(false);
  useHttpQuery(
    'resend-email',
    API_URLS.RESEND_VERIFICATION_EMAIL,
    'get',
    {},
    {
      enabled,
      onSuccess: () => {
        setFetch(false);
        addAlert({
          message: 'Email has been resent',
          type: 'success',
        });
      },
      onError: () => {
        setFetch(false);
        addAlert({
          message: 'Error resending email',
          type: 'error',
        });
      },
    }
  );

  const { addAlert } = useAlert();

  const onClick = () => {
    setFetch(true);
  };

  return (
    <Button
      variant="text"
      onClick={onClick}
      disabled={enabled}
      endIcon={<ReplayIcon />}
    >
      {enabled ? 'Sending…' : texts.resendButton}
    </Button>
  );
};
